/**
 * useAskUploads — the Ask composer's file-attachment lane (D32-lite, upload spec §5.5 SPA half).
 *
 * The server side already ships: POST/GET /api/surfaces/uploads distills a dropped file into a
 * chat-scoped note the answer path (searchAttachments, U6) can read. This hook is the client half —
 * the paperclip/drag-drop entry point, the per-file status chips, and the honest limits.
 *
 * CHAT BINDING. An upload belongs to a chat. But at bare /ask the chat is not minted until the first
 * answer persists — so a file dropped into a brand-new thread would have nothing to attach to. The
 * route solves this with mint-on-attach (`newChat`): when there is no chatId, the POST mints one and
 * returns it, and we adopt it as the active thread (`adoptChat`) so the first question already carries
 * it and the URL becomes /ask/<chatId>. Everything here keys on that chat id.
 *
 * HONEST LIMITS (bug-664). The route's EFFECTIVE per-file cap is ~4 MiB (the platform request-body
 * ceiling), not the 64 MiB policy number. We pre-check against the effective cap for instant feedback
 * and let the server's typed failure_reason be authoritative — a rejected file lands as a `rejected`
 * chip carrying its reason, never a silent drop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUpload, apiFetch, ApiError } from '../lib/api';

// A file's lifecycle in the chip. Mirrors user_uploads.status (migration 169) plus the client-only
// `uploading` state, which is the window between the user's drop and the server's first row.
export type UploadStatus = 'uploading' | 'queued' | 'extracting' | 'distilling' | 'ready' | 'failed' | 'rejected';

export interface UploadItem {
  // The server row id once it exists; a client temp id (`tmp-…`) while the POST is in flight, so a chip
  // can render the instant the user drops, before the server has minted anything.
  uploadId: string;
  filename: string;
  status: UploadStatus;
  failureReason: string | null;
}

// The route's `limits` payload (a subset — what the UI shows/enforces). Server is authoritative; these
// mirror the route so we can pre-check before the first POST tells us the real numbers.
export interface UploadLimits {
  maxFiles: number;
  maxBytes: number; // the EFFECTIVE per-file cap (~4 MiB), what we actually enforce
  policyMaxFileBytes: number; // the 64 MiB policy number, shown as the stated ceiling
  minBytes: number;
  extensions: string[];
}

// Mirror of lib/uploads/limits.ts, used ONLY for instant client pre-flight before the server's `limits`
// payload arrives. The server re-checks and is authoritative — these can never be laxer than the route.
const CLIENT_LIMITS: UploadLimits = {
  maxFiles: 5,
  maxBytes: 4 * 1024 * 1024 - 8 * 1024, // EFFECTIVE_MAX_FILE_BYTES: min(64MiB, 4MiB - framing)
  policyMaxFileBytes: 64 * 1024 * 1024,
  minBytes: 1024,
  extensions: ['.pdf', '.docx', '.xlsx', '.pptx', '.csv', '.tsv', '.md', '.txt', '.json', '.xml', '.html'],
};

const UPLOADS_PATH = '/api/surfaces/uploads';
const POLL_MS = 2500;
const TERMINAL: ReadonlySet<UploadStatus> = new Set(['ready', 'failed', 'rejected']);

// The client-side pre-flight (spec §7): instant feedback before the POST, against the SAME rules the
// route enforces. Pure and exported so it is unit-testable without a render harness — the honest-limits
// enforcement is exactly the thing a "looks fine" implementation gets subtly wrong. Returns a friendly
// message to show, or null when every file passes. The server RE-CHECKS and is authoritative; this can
// never be laxer than the route (a file this lets through may still land as a `rejected` chip).
export function preflightAttach(files: File[], alreadyAttached: number, limits: UploadLimits): string | null {
  if (files.length === 0) return null;
  if (alreadyAttached + files.length > limits.maxFiles) return `Up to ${limits.maxFiles} files per chat.`;
  const tooBig = files.find((f) => f.size > limits.maxBytes);
  if (tooBig) return `"${tooBig.name}" is too large — max ~${Math.floor(limits.maxBytes / (1024 * 1024))} MB per file.`;
  const tooSmall = files.find((f) => f.size < limits.minBytes);
  if (tooSmall) return `"${tooSmall.name}" is too small (min 1 KB).`;
  const badType = files.find((f) => !limits.extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
  if (badType) return `"${badType.name}" is not a supported type.`;
  return null;
}

// Human-readable, honest failure text. The user must learn which rule they hit (spec §7); a typed
// reason from the server maps here, and an unknown reason degrades to a plain sentence, never a code.
export function uploadFailureText(reason: string | null): string {
  switch (reason) {
    case 'too_large':
    case 'request_too_large':
      return 'Too large (max ~4 MB per file)';
    case 'too_small':
      return 'Too small (min 1 KB)';
    case 'unsupported_type':
      return 'Unsupported file type';
    case 'content_mismatch':
    case 'unsupported_encoding':
      return "File contents didn't match its type";
    case 'daily_file_limit':
    case 'daily_byte_limit':
      return 'Daily upload limit reached';
    case 'no_text_layer':
      return 'No readable text (a scan?)';
    case 'extraction_failed':
    case 'extracted_too_large':
      return "Couldn't read this file";
    case 'distill_failed':
      return "Couldn't index this file";
    case 'capture_paused':
      return 'Uploads are paused';
    default:
      return reason ? 'Upload failed' : 'Failed';
  }
}

// The send gate's predicate (bug-721): TRUE while any attachment is non-terminal
// (uploading/queued/extracting/distilling). Pure and exported for the unit test — the composer blocks
// send on exactly this, so its edges (failed/rejected never block; empty never blocks) must be pinned.
export function uploadsBlockSend(uploads: readonly Pick<UploadItem, 'status'>[]): boolean {
  return uploads.some((u) => !TERMINAL.has(u.status));
}

export interface UseAskUploads {
  uploads: UploadItem[];
  limits: UploadLimits;
  attaching: boolean;
  // A client-side pre-flight refusal (too many, too big, wrong type) shown inline; cleared on the next
  // attach. The SERVER refusals arrive as `rejected` chips instead — this is only the instant local one.
  attachError: string | null;
  attachFiles: (files: File[]) => void;
  dismiss: (uploadId: string) => void;
  // TRUE when at least one upload is still working (uploading/queued/extracting/distilling). Drives the
  // composer's SEND GATE (bug-721, Teddy 2026-08-12: a question must not fire before its file has
  // landed — the engine only registers searchAttachments for READY uploads) and the status line that
  // says so. Supersedes U9 §5.5's disclose-don't-block posture at the composer; the honest-notice
  // frames stay as belt-and-suspenders for resumed threads and races.
  indexing: boolean;
}

/**
 * @param activeChatId the current Ask thread, or null for a not-yet-minted one.
 * @param adoptChat     called with the id the route mints on a bare-thread attach, so the thread and its
 *                      URL follow the file. A no-op when the attach already had a chatId.
 */
export function useAskUploads(activeChatId: string | null, adoptChat: (id: string) => void): UseAskUploads {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [limits, setLimits] = useState<UploadLimits>(CLIENT_LIMITS);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  // The chat id the `uploads` state currently reflects. It follows activeChatId, EXCEPT across a
  // mint-on-attach: attachFiles sets it to the freshly minted id BEFORE adoptChat propagates activeChatId
  // back, so the thread-switch effect below does not wipe the chips we just showed for that same chat.
  const reflectedChatIdRef = useRef<string | null>(activeChatId);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A monotonic thread-switch counter, bumped whenever the active thread changes (below). attachFiles
  // captures it at the drop; if it has moved by the time the POST resolves, the user switched threads
  // mid-upload — so the resolved chips are NOT written into the now-current thread and the mint is NOT
  // adopted (adoptChat also refuses to clobber a real thread). The bare→bare "New chat" case (null→null)
  // does not fire the switch effect and is benign: the fresh bare thread simply becomes the file's chat.
  const threadGenRef = useRef(0);
  // Single-flight for the bare-thread mint: while the first no-chat attach is minting, this holds a
  // promise of its chat id so concurrent attaches bind to the SAME chat instead of each minting one.
  const mintPromiseRef = useRef<Promise<string | null> | null>(null);
  // Chips the user dismissed (bug-721 escape hatch: a WORKING chip is dismissable so a stranded server
  // row can never brick the send gate). The poll re-lists the server's rows wholesale, so without this
  // set a dismissed chip would resurrect on the next tick. Client-view-only, cleared on thread switch —
  // the server row is untouched (per-file deletion is U7's purge arm, not this affordance).
  const dismissedRef = useRef<Set<string>>(new Set());
  const withoutDismissed = useCallback((items: UploadItem[]) => items.filter((u) => !dismissedRef.current.has(u.uploadId)), []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Poll a chat's upload rows until every one is terminal. Keyed on a chat id captured at scheduling time,
  // so a poll that resolves after the user switched threads is discarded rather than written into the new
  // thread's chips (the same generation-guard shape useAskQuery uses for persists).
  const pollOnce = useCallback(
    (chatId: string) => {
      stopPolling();
      pollTimerRef.current = setTimeout(async () => {
        if (reflectedChatIdRef.current !== chatId) return; // switched away — abandon this loop
        try {
          const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
            `${UPLOADS_PATH}?chatId=${encodeURIComponent(chatId)}`,
          );
          if (reflectedChatIdRef.current !== chatId) return;
          const items = withoutDismissed((rows ?? []).map(fromServer));
          setUploads(items);
          if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(chatId);
        } catch {
          // A transient poll failure is not terminal — try again on the next tick rather than freezing a
          // chip at "extracting" forever. If the chat changed, the guard above stops the loop.
          if (reflectedChatIdRef.current === chatId) pollOnce(chatId);
        }
      }, POLL_MS);
    },
    [stopPolling],
  );

  // Thread switched (newChat/loadChat/delete-active) → reset to that chat's stored uploads. A brand-new
  // thread (null) clears the chips. The reflectedChatIdRef guard skips the reset when activeChatId is
  // just catching up to a chat we already minted-and-attached into (no flicker, no double load).
  useEffect(() => {
    if (reflectedChatIdRef.current === activeChatId) return;
    reflectedChatIdRef.current = activeChatId;
    threadGenRef.current += 1; // a real thread switch — invalidate any in-flight attach's adoption/chips
    mintPromiseRef.current = null; // a stale bare-thread mint must not bind a new thread's attaches
    dismissedRef.current = new Set(); // dismissals are per-thread view state
    stopPolling();
    setUploads([]);
    setAttachError(null);
    setAttaching(false); // a switch frees the paperclip immediately, without waiting on the old POST
    if (!activeChatId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
          `${UPLOADS_PATH}?chatId=${encodeURIComponent(activeChatId)}`,
        );
        if (cancelled || reflectedChatIdRef.current !== activeChatId) return;
        const items = withoutDismissed((rows ?? []).map(fromServer));
        setUploads(items);
        if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(activeChatId);
      } catch {
        // Best-effort: a failed list load just leaves the chip row empty for a resumed thread.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChatId, pollOnce, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const attachFiles = useCallback(
    (files: File[]) => {
      setAttachError(null);
      if (files.length === 0) return;

      // CLIENT PRE-FLIGHT (instant feedback; the server re-checks and is authoritative). A rejected/failed
      // chip does not count toward the ceiling — only live attachments do.
      const already = uploads.filter((u) => u.status !== 'rejected' && u.status !== 'failed').length;
      const preflightError = preflightAttach(files, already, limits);
      if (preflightError) {
        setAttachError(preflightError);
        return;
      }

      // Optimistic chips so the user sees their files the instant they drop, before the POST resolves.
      const temps: UploadItem[] = files.map((f, i) => ({
        uploadId: `tmp-${Date.now()}-${i}`,
        filename: f.name,
        status: 'uploading',
        failureReason: null,
      }));
      setUploads((prev) => [...prev, ...temps]);
      setAttaching(true);

      const chatIdAtAttach = activeChatId;
      const genAtAttach = threadGenRef.current;

      // SINGLE-FLIGHT THE BARE-THREAD MINT. Two drops inside the first POST's latency window both read
      // activeChatId === null and would each send newChat=1 — the server mints TWO chats, the composer
      // ends up showing one file while the question posts the OTHER chat's id, and the shown file is
      // exactly the one the answer cannot read. So only the FIRST concurrent bare-thread attach mints;
      // the rest wait for its chat (decided synchronously here, so a second attachFiles in the same tick
      // sees the in-flight mint) and bind to the SAME id.
      let waitForMint: Promise<string | null> | null = null;
      let ownsMint = false;
      let resolveMint: ((id: string | null) => void) | undefined;
      if (chatIdAtAttach === null) {
        if (mintPromiseRef.current) {
          waitForMint = mintPromiseRef.current;
        } else {
          ownsMint = true;
          mintPromiseRef.current = new Promise<string | null>((r) => {
            resolveMint = r;
          });
        }
      }

      void (async () => {
        try {
          // A non-owner bare-thread attach waits for the owner's minted chat, then binds to it instead of
          // minting again. Null back means the owner's mint FAILED — fall back to minting ourselves.
          let boundChatId = chatIdAtAttach;
          if (waitForMint) {
            boundChatId = await waitForMint;
            if (threadGenRef.current !== genAtAttach) return; // switched threads while waiting
          }

          const form = new FormData();
          for (const f of files) form.append('files', f);
          if (boundChatId) form.append('chatId', boundChatId);
          else form.append('newChat', '1'); // the mint owner (or a waiter whose owner-mint failed)

          const res = await apiUpload<UploadPostResponse>(UPLOADS_PATH, form);

          // Hand the minted id to any waiters BEFORE the thread-switch guard below can `return` — a waiter
          // parked on this promise must be released even if we ourselves have since switched away.
          if (ownsMint) {
            resolveMint?.(res.chatId ?? null);
            mintPromiseRef.current = null;
          }

          // The user switched to a REAL thread while this upload was in flight → its chips and its mint
          // belong to a thread that is no longer showing. Do not write them into the now-current thread
          // and do not adopt the mint (adoptChat would refuse the clobber anyway). The upload still landed
          // server-side, bound to the chat it was minted for; it re-appears if the user returns to it.
          if (threadGenRef.current !== genAtAttach) return;

          if (res.limits) setLimits((prev) => ({ ...prev, ...res.limits }));

          const mintedChatId = res.chatId ?? boundChatId;
          // Adopt a minted chat BEFORE writing chips, and point reflectedChatIdRef at it, so the
          // thread-switch effect treats the incoming activeChatId change as already-reflected. Compared
          // against `boundChatId` (the chat this attach was actually bound to — a waiter's bound id is the
          // owner's mint, so it correctly does NOT re-adopt).
          if (mintedChatId && mintedChatId !== boundChatId) {
            reflectedChatIdRef.current = mintedChatId;
            adoptChat(mintedChatId);
          }

          // Replace the temp chips with the server's authoritative rows for this chat (a re-list is the
          // simplest correct reconciliation — dedupe, duplicates, and rejections all resolve server-side).
          if (mintedChatId) {
            try {
              const { uploads: rows } = await apiFetch<{ uploads: ServerUpload[] }>(
                `${UPLOADS_PATH}?chatId=${encodeURIComponent(mintedChatId)}`,
              );
              if (reflectedChatIdRef.current === mintedChatId) {
                const items = withoutDismissed((rows ?? []).map(fromServer));
                setUploads(items);
                if (items.some((u) => !TERMINAL.has(u.status))) pollOnce(mintedChatId);
              }
            } catch {
              // The POST succeeded; a failed re-list just leaves the optimistic chips until the next poll.
              if (reflectedChatIdRef.current === mintedChatId) pollOnce(mintedChatId);
            }
          }
        } catch (e) {
          // The mint owner failed → release waiters with null so they fall back to minting themselves
          // (an already-failing path; a fresh mint is the right recovery). Clear the ref either way.
          if (ownsMint) {
            resolveMint?.(null);
            mintPromiseRef.current = null;
          }
          // The POST itself failed (network, or a request-level refusal like too_many_files). Mark the
          // temp chips failed with the server's typed reason where we have it — never a silent removal.
          // Same switch guard: a failure that resolves after a thread switch must not stamp a rejected
          // chip onto the thread the user moved to.
          if (threadGenRef.current !== genAtAttach) return;
          const reason = e instanceof ApiError ? ((e.body as { error?: string })?.error ?? null) : null;
          setUploads((prev) =>
            prev.map((u) =>
              temps.some((t) => t.uploadId === u.uploadId) ? { ...u, status: 'rejected' as const, failureReason: reason } : u,
            ),
          );
        } finally {
          // Always clear — `attaching` is a global "an upload POST is in flight" flag (it disables the
          // paperclip), not thread-scoped state, so clearing it after a switch is correct.
          setAttaching(false);
        }
      })();
    },
    [activeChatId, adoptChat, limits, pollOnce, uploads],
  );

  const dismiss = useCallback((uploadId: string) => {
    // Client-only: drops a chip from view — a rejected/failed one the user has read, or (bug-721) a
    // WORKING one the user unattaches to lift the send gate. It does not delete the server row —
    // per-file server deletion is U7's purge arm, not this composer affordance. Remembered in
    // dismissedRef so the poll's wholesale re-list cannot resurrect it.
    dismissedRef.current.add(uploadId);
    setUploads((prev) => prev.filter((u) => u.uploadId !== uploadId));
  }, []);

  const indexing = uploadsBlockSend(uploads);

  return { uploads, limits, attaching, attachError, attachFiles, dismiss, indexing };
}

// ── server shapes (GET/POST /api/surfaces/uploads) ─────────────────────────────────────────────
interface ServerUpload {
  uploadId: string;
  filename: string;
  status: UploadStatus;
  failureReason: string | null;
}
interface UploadPostResponse {
  uploads: (ServerUpload & { duplicate?: boolean })[];
  chatId: string | null;
  limits?: UploadLimits;
}
function fromServer(r: ServerUpload): UploadItem {
  return { uploadId: r.uploadId, filename: r.filename, status: r.status, failureReason: r.failureReason ?? null };
}
